import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import { UserTable } from '../components/UserTable';
import { UserFilters } from '../components/UserFilters';
import { UserForm } from '../components/UserForm';
import { useUsers } from '../hooks/useUsers';
import { Button } from '@/ui/components/atoms/Button';
import type { User, UserFilters as UserFiltersType } from '../types';

const UsersTab: React.FC = () => {
  const { t } = useTranslation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [filters, setFilters] = useState<UserFiltersType>({
    role: 'all',
    status: 'all',
    search: ''
  });
  const [activeFilterKey, setActiveFilterKey] = useState('role-all');
  const { data: users, isLoading: loading, error, refetch } = useUsers(filters);

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingUser(null);
  };

  return (
    <div className="users-tab-container">
      {/* Header */}
      <div className="users-tab-header">
        <div className="users-tab-header-content">
          <div className="users-tab-title-row">
            <div>
              <h2 className="users-tab-title">
                {t('admin.userManagement.users.title', 'Liste des Utilisateurs')}
              </h2>
              <p className="users-tab-subtitle">
                {t('admin.userManagement.users.subtitle', 'Gérez les comptes utilisateurs et leurs permissions')}
              </p>
            </div>
            <div className="users-tab-action-wrapper">
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                className="users-tab-create-btn"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                {t('admin.userManagement.createUser', 'Créer un utilisateur')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="users-tab-content">
        {/* Error Display */}
        {error && (
          <div className="users-tab-error">
            <div className="alert alert--error">
              <span>{(error as Error).message}</span>
              <button
                onClick={() => refetch()}
                className="alert__close"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Filters Section */}
        <UserFilters
          onFilterChange={setFilters}
          activeFilter={activeFilterKey}
          onActiveFilterChange={setActiveFilterKey}
          resultsCount={users?.length || 0}
        />

        {/* Users Table */}
        <UserTable
          users={users || []}
          loading={loading}
          error={error as Error | null}
          onEdit={handleEdit}
          onRefresh={refetch}
        />

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <UserForm
            user={editingUser}
            onClose={handleCloseModal}
            onSuccess={() => {
              handleCloseModal();
              refetch();
            }}
          />
        )}
      </div>
    </div>
  );
};

export { UsersTab };
export default UsersTab;
